import {
  type EmailSignaturesActions,
  initialEmailSignatureContextValues,
} from '@reducers/email-signature-reducer';
import { useCallback, useState } from 'react';

import { EmailSignatureFieldTypes } from '../../../consts';
// types
import type { EmailSignatureContactDetailsFieldId, EmailSignatureV2 } from '../../../types';
// utils
import type EmailSignatureImagesUploader from '../../EmailSignatureImagesUploader';
// hooks and context
import useContactDetailsIconsGenerator from '../../useContactDetailsIconsGenerator';
import useInitialEmailSignature from '../../useInitialEmailSignature';
import { useContactIconsGeneratorSwitch } from '../useIconGeneratorSwitches';

interface SideEditorContactServices {
  hasEditedFieldWidth: boolean;
  onEditFieldWidth: () => void;
}

interface ContactServicesProps {
  emailSignature: EmailSignatureV2;
  emailSignatureDispatch: (action: EmailSignaturesActions) => void;
  emailSignatureHasBeenLoaded: boolean;
  handleOnErrorUploading: (fieldType: EmailSignatureFieldTypes) => void;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
}

/*
    This component is a context wrapper that provides all the core services for the email signature side editor,
    it should be used with the email signature Side Editor component. This component allows SideEditor component to
    be reused in different editors (currently used in EmailSignatureMainEditor and EmailSignatureOnboardingEditor)
*/

function useContactServices({
  emailSignature,
  emailSignatureDispatch,
  emailSignatureHasBeenLoaded,
  handleOnErrorUploading,
  uploadEmailSignatureFieldImagesThunk,
}: ContactServicesProps): SideEditorContactServices {
  const [hasEditedFieldWidth, setHasEditedFieldWidth] = useState<boolean>(false);
  const onEditFieldWidth = useCallback(() => setHasEditedFieldWidth(true), []);
  const { initialEmailSignature, initialEmailSignatureHasBeenLoaded } = useInitialEmailSignature(
    emailSignature,
    emailSignatureHasBeenLoaded,
  );

  const turnOnContactDetailsIconsGenerator = useContactIconsGeneratorSwitch(
    emailSignatureHasBeenLoaded,
    initialEmailSignatureHasBeenLoaded,
    initialEmailSignature || initialEmailSignatureContextValues,
    emailSignature,
  );

  const handleUpdateNewLocalContactIconUrl = useCallback(
    (contactFieldId: EmailSignatureContactDetailsFieldId, localImageUrl: string) => {
      emailSignatureDispatch({
        type: 'UPDATE_CONTACT_DETAIL_FIELD_ICON_URL',
        value: {
          fieldId: contactFieldId,
          updatedFieldValue: localImageUrl,
        },
      });

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
        emailSignatureImagesUploader.uploadImage(
          contactFieldId,
          localImageUrl,
          EmailSignatureFieldTypes.CONTACT,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_CONTACT_DETAIL_FIELD',
              value: contactFieldId,
            });
          },
        );
      });
    },
    [emailSignatureDispatch, uploadEmailSignatureFieldImagesThunk, handleOnErrorUploading],
  );

  useContactDetailsIconsGenerator(
    emailSignature,
    handleUpdateNewLocalContactIconUrl,
    turnOnContactDetailsIconsGenerator,
  );

  return {
    onEditFieldWidth,
    hasEditedFieldWidth,
  };
}

export { useContactServices };
